import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getEffectiveAuthToken } from '../api/learnApiClient';

const LEARN_API_BASE_URL = import.meta.env.VITE_LEARN_API_BASE_URL || 'http://localhost:5002/api/learn';

interface UseLearnBackendDataProps {
  templateKey?: string;
  metricKey?: string;
  queryConfig?: {
    data_source?: string;
  };
  chartType?: string;
  enabled?: boolean;
}

/** Map API progress_status to LearningPlanCard state labels */
const mapProgressStatus = (status: string | undefined): string => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'in_progress': return 'On Going';
    case 'enrolled':
    case 'not_started': return 'Not Started';
    default: return status || 'Not Started';
  }
};

/**
 * Fetches data directly from learn-backend for the mp_learning_plan widget only.
 * All other learn_api:* widgets route through the analytics backend via useAnalyticsData.
 */
export const useLearnBackendData = ({
  templateKey,
  metricKey,
  queryConfig,
  chartType,
  enabled = true,
}: UseLearnBackendDataProps) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only handle mp_learning_plan — all other learn widgets go through analytics backend
  const isLearningPlanWidget = useMemo(() => {
    return templateKey === 'mp_learning_plan' || chartType === 'learning_plan_card';
  }, [templateKey, chartType]);

  useEffect(() => {
    if (!isLearningPlanWidget || !enabled) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getEffectiveAuthToken();
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        const response = await axios.get(`${LEARN_API_BASE_URL}/v1/lms/my-courses`, {
          headers,
          timeout: 10000,
        });

        const courses: any[] = response.data?.courses || [];

        const enrollments = courses.map((c: any) => ({
          id: c.id,
          course_name: c.course_title || '',
          state: mapProgressStatus(c.progress_status),
          progress_percentage: c.progress_percent ?? 0,
          due_date: c.due_date?.Valid ? c.due_date.Time : undefined,
          mode: c.course_mode?.Valid ? c.course_mode.String : undefined,
          category: c.assignment_category?.Valid ? c.assignment_category.String : undefined,
          duration_in_minutes: c.course_duration_minutes?.Valid ? c.course_duration_minutes.Int32 : undefined,
        }));

        setData({ enrollments, stats: null });
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError('Session expired. Please log in again.');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to view this data.');
        } else if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please try again.');
        } else if (err.message?.includes('Network Error')) {
          setError('Unable to connect to the server.');
        } else {
          setError(err.response?.data?.message || 'Failed to load learning plan data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isLearningPlanWidget, enabled]);

  return { data, loading, error };
};
